/**
 * TodoContext - Central state management for the todo tree application
 *
 * Data Flow Architecture:
 * 1. App loads JSON file → converts to flat TodoTree structure
 * 2. TodoProvider wraps components with tree state and operations
 * 3. Components call context methods to update tree state
 * 4. Context validates operations (e.g., parent completion rules)
 * 5. State changes trigger re-render with new tree structure
 * 6. Tree changes propagate back to parent for file export
 *
 * The flat tree structure (TodoTree) is used internally for efficient lookups,
 * while the nested format is used for file import/export.
 */
import React, { createContext, useContext, useState, useCallback, useRef, useMemo, useEffect } from 'react';
import { TodoTree, TodoItem, TodayList, Priority, Visibility } from '../types/todo';
import { areAllChildrenCompleted, getIncompleteChildren, findAllParentIds, canAddChildToNode } from '../utils/tree';
import { generateUUID } from '../utils/uuid';
import { useTreeHistory, HistorySnapshot } from '../hooks/useTreeHistory';
import {
  EMPTY_TODAY_LIST,
  getLocalDateString,
  isEligibleForToday,
  isTodayListCurrent,
  pruneTodayItems,
} from '../utils/today';

interface TodoContextType {
  tree: TodoTree;
  setTree: (treeOrUpdater: TodoTree | ((prevTree: TodoTree) => TodoTree)) => void;
  updateItem: (id: string, updates: Partial<TodoItem>) => void;
  toggleDone: (id: string, onBlocked?: (incompleteChildren: Array<{ id: string; item: TodoItem }>) => void) => void;
  toggleBlocked: (id: string) => void;
  toggleArchived: (id: string) => void;
  toggleHighlighted: (id: string) => void;
  unarchiveItem: (id: string) => void;
  setPriority: (id: string, priority: Priority) => void;
  setTimeEstimate: (id: string, hours: number | undefined) => void;
  updateName: (id: string, name: string) => void;
  updateDescription: (id: string, description: string) => void;
  setVisibility: (id: string, visibility: Visibility) => void;
  addChild: (parentId: string, childName: string, onParentsMarkedIncomplete?: (parentIds: string[]) => void, onBlocked?: (message: string) => void) => void;
  removeItem: (id: string) => void;
  moveItem: (itemId: string, newParentId: string, newIndex?: number) => void;
  reorderChildren: (parentId: string, newChildrenOrder: string[]) => void;
  onTreeChange?: (tree: TodoTree) => void;
  // Day plan ("Today" list)
  today: TodayList;
  /** True when the plan was made for an earlier day (or never made). */
  isDayPlanStale: boolean;
  /** True when there is at least one task worth planning, or a plan already exists. */
  hasPlannableWork: boolean;
  addToToday: (id: string) => void;
  removeFromToday: (id: string) => void;
  reorderToday: (items: string[]) => void;
  /** Replace the plan with `items` and stamp it as made for today. */
  commitDayPlan: (items: string[]) => void;
  // Undo/redo history
  undo: () => void;
  redo: () => void;
  canUndo: boolean;
  canRedo: boolean;
  // Height tracking for ResizeObserver
  actualHeights: Map<string, number>;
  reportNodeHeight: (id: string, height: number) => void;
}

const TodoContext = createContext<TodoContextType | undefined>(undefined);

export function useTodoContext() {
  const context = useContext(TodoContext);
  if (!context) {
    throw new Error('useTodoContext must be used within a TodoProvider');
  }
  return context;
}

interface TodoProviderProps {
  children: React.ReactNode;
  initialTree: TodoTree;
  initialToday?: TodayList;
  onTreeChange?: (tree: TodoTree) => void;
  onTodayChange?: (today: TodayList) => void;
}

export function TodoProvider({ children, initialTree, initialToday, onTreeChange, onTodayChange }: TodoProviderProps) {
  const [tree, setTreeState] = useState<TodoTree>(initialTree);
  // A plan loaded from disk can reference tasks that were archived or deleted
  // by another editor, so it is pruned before it ever reaches the UI.
  const [today, setTodayState] = useState<TodayList>(
    () => pruneTodayItems(initialToday ?? EMPTY_TODAY_LIST, initialTree)
  );
  const [actualHeights, setActualHeights] = useState<Map<string, number>>(new Map());

  // History resets on file load for free: App remounts the provider via a
  // key tied to the loaded file, which reinitializes this hook.
  const { push: pushHistory, undo: historyUndo, redo: historyRedo, canUndo, canRedo } = useTreeHistory();

  // Mirrors of the latest state so updaters and side effects (history capture,
  // autosave) can run outside React's setState updater. StrictMode double-
  // invokes updaters in dev, so side effects inside them would fire twice.
  const treeRef = useRef<TodoTree>(initialTree);
  const todayRef = useRef<TodayList>(today);

  /**
   * Apply a new tree and/or day plan as one undoable step.
   *
   * Each half is compared by reference, so a change to one never causes a
   * spurious save or re-render of the other, and a call that changes nothing
   * is dropped before it can pollute the undo stack.
   */
  const commit = useCallback((nextTree: TodoTree, nextToday: TodayList) => {
    const prevTree = treeRef.current;
    const prevToday = todayRef.current;
    if (nextTree === prevTree && nextToday === prevToday) return;

    pushHistory({ tree: prevTree, today: prevToday });

    if (nextTree !== prevTree) {
      treeRef.current = nextTree;
      setTreeState(nextTree);
      onTreeChange?.(nextTree);
    }
    if (nextToday !== prevToday) {
      todayRef.current = nextToday;
      setTodayState(nextToday);
      onTodayChange?.(nextToday);
    }
  }, [pushHistory, onTreeChange, onTodayChange]);

  const setTree = useCallback((newTreeOrUpdater: TodoTree | ((prevTree: TodoTree) => TodoTree)) => {
    const prevTree = treeRef.current;
    const newTree = typeof newTreeOrUpdater === 'function' ? newTreeOrUpdater(prevTree) : newTreeOrUpdater;

    // No-op mutations (missing ids, unchanged values) return the same object;
    // skip them so they don't pollute history or trigger a save.
    if (newTree === prevTree) return;

    // Archiving or deleting a planned task also takes it off the day's list.
    commit(newTree, pruneTodayItems(todayRef.current, newTree));
  }, [commit]);

  /** Restore both halves of a history snapshot, notifying only what changed. */
  const applySnapshot = useCallback((snapshot: HistorySnapshot) => {
    if (snapshot.tree !== treeRef.current) {
      treeRef.current = snapshot.tree;
      setTreeState(snapshot.tree);
      onTreeChange?.(snapshot.tree);
    }
    if (snapshot.today !== todayRef.current) {
      todayRef.current = snapshot.today;
      setTodayState(snapshot.today);
      onTodayChange?.(snapshot.today);
    }
  }, [onTreeChange, onTodayChange]);

  const undo = useCallback(() => {
    const snapshot = historyUndo({ tree: treeRef.current, today: todayRef.current });
    if (!snapshot) return;
    applySnapshot(snapshot);
  }, [historyUndo, applySnapshot]);

  const redo = useCallback(() => {
    const snapshot = historyRedo({ tree: treeRef.current, today: todayRef.current });
    if (!snapshot) return;
    applySnapshot(snapshot);
  }, [historyRedo, applySnapshot]);

  const updateItem = useCallback((id: string, updates: Partial<TodoItem>) => {
    setTree((prevTree: TodoTree) => {
      const existing = prevTree[id];
      if (!existing) return prevTree;

      // Skip updates that change nothing (e.g. re-saving an unedited name)
      // so they don't create phantom undo history entries.
      const hasChange = (Object.keys(updates) as Array<keyof TodoItem>).some(
        key => existing[key] !== updates[key]
      );
      if (!hasChange) return prevTree;

      return {
        ...prevTree,
        [id]: {
          ...existing,
          ...updates,
        },
      };
    });
  }, [setTree]);

  /**
   * Toggle completion status with business rule enforcement
   *
   * Key Business Rule: Parent tasks cannot be marked complete until ALL children are complete.
   * This maintains data integrity and prevents inconsistent states in the hierarchy.
   *
   * Flow:
   * 1. Check if task is being marked complete (!item.done)
   * 2. If so, validate all children are complete
   * 3. If validation fails, call onBlocked callback with incomplete children
   * 4. Otherwise, toggle the completion status
   * 5. When marking as done, automatically unhighlight the node
   */
  const toggleDone = useCallback((id: string, onBlocked?: (incompleteChildren: Array<{ id: string; item: TodoItem }>) => void) => {
    const item = tree[id];
    if (!item) return;

    // Enforce parent completion rule: can't mark parent done if children are incomplete
    if (!item.done) {
      if (!areAllChildrenCompleted(tree, id)) {
        const incompleteChildren = getIncompleteChildren(tree, id);
        onBlocked?.(incompleteChildren);
        return; // Block the operation and notify UI
      }
    }

    // When marking as done, also remove highlight
    const updates: Partial<TodoItem> = { done: !item.done };
    if (!item.done) {
      updates.highlighted = false;
    }

    updateItem(id, updates);
  }, [updateItem, tree]);

  const toggleBlocked = useCallback((id: string) => {
    updateItem(id, { blocked: !tree[id]?.blocked });
  }, [updateItem, tree]);

  const toggleArchived = useCallback((id: string) => {
    updateItem(id, { archived: !tree[id]?.archived });
  }, [updateItem, tree]);

  const unarchiveItem = useCallback((id: string) => {
    updateItem(id, { archived: false });
  }, [updateItem]);

  const toggleHighlighted = useCallback((id: string) => {
    updateItem(id, { highlighted: !tree[id]?.highlighted });
  }, [updateItem, tree]);

  const setPriority = useCallback((id: string, priority: Priority) => {
    updateItem(id, { priority });
  }, [updateItem]);

  const setTimeEstimate = useCallback((id: string, hours: number | undefined) => {
    updateItem(id, { timeEstimate: hours });
  }, [updateItem]);

  const updateName = useCallback((id: string, name: string) => {
    updateItem(id, { name });
  }, [updateItem]);

  const updateDescription = useCallback((id: string, description: string) => {
    updateItem(id, { description });
  }, [updateItem]);

  const setVisibility = useCallback((id: string, visibility: Visibility) => {
    setTree((prevTree: TodoTree) => {
      if (!prevTree[id]) return prevTree;
      return {
        ...prevTree,
        [id]: {
          ...prevTree[id],
          visibility,
        },
      };
    });
  }, [setTree]);

  /**
   * Add a new child task with automatic parent state management
   *
   * Business Logic:
   * - Cannot add child to a node with a time estimate
   * - When adding a child to a completed parent, the parent (and all ancestors)
   *   must be marked as incomplete to maintain hierarchy consistency
   * - This prevents the logical inconsistency of "completed" parents having new work
   *
   * Flow:
   * 1. Check if parent has time estimate - block if so
   * 2. Create new child with default properties
   * 3. Check if parent is marked complete
   * 4. If so, find all ancestors and mark them incomplete
   * 5. Add child to parent's children array
   * 6. Update tree state atomically
   * 7. Notify UI of any parent status changes
   */
  const addChild = useCallback((parentId: string, childName: string, onParentsMarkedIncomplete?: (parentIds: string[]) => void, onBlocked?: (message: string) => void) => {
    const parent = tree[parentId];

    if (!parent) return;

    // Block if parent has a time estimate
    if (!canAddChildToNode(tree, parentId)) {
      onBlocked?.('Cannot add child to a task with a time estimate. Remove the estimate first.');
      return;
    }

    const newChildId = generateUUID();

    // Create new child with sensible defaults
    const newChild: TodoItem = {
      name: childName,
      children: [],
      archived: false,
      done: false,
      blocked: false,
      priority: 'low',
      visibility: 'show-active',
      description: '',
      highlighted: false,
    };

    // Cascade completion state changes up the tree
    // If parent is complete, adding work means it's no longer complete
    const parentsToUnmark: string[] = [];
    if (parent.done) {
      const allParentIds = findAllParentIds(tree, parentId);
      allParentIds.push(parentId); // Include the direct parent
      parentsToUnmark.push(...allParentIds);
    }

    // Build new tree state atomically to prevent intermediate invalid states
    const newTree = {
      ...tree,
      [newChildId]: newChild,
      [parentId]: {
        ...parent,
        children: [...parent.children, newChildId],
      },
    };

    // Apply completion state changes to maintain consistency
    parentsToUnmark.forEach(pid => {
      if (newTree[pid]) {
        newTree[pid] = { ...newTree[pid], done: false };
      }
    });

    setTree(newTree);

    // Notify UI layer of state changes for user feedback
    if (parentsToUnmark.length > 0) {
      onParentsMarkedIncomplete?.(parentsToUnmark);
    }
  }, [tree, setTree]);

  const removeItem = useCallback((id: string) => {
    const itemToRemove = tree[id];
    if (!itemToRemove) return;

    // Remove all descendants recursively
    const toRemove = new Set([id]);
    const queue = [id];

    while (queue.length > 0) {
      const currentId = queue.shift()!;
      const item = tree[currentId];
      if (item) {
        item.children.forEach(childId => {
          toRemove.add(childId);
          queue.push(childId);
        });
      }
    }

    // Remove from parent's children array
    const newTree = { ...tree };
    for (const [parentId, parent] of Object.entries(newTree)) {
      if (parent.children.includes(id)) {
        newTree[parentId] = {
          ...parent,
          children: parent.children.filter(childId => childId !== id),
        };
        break;
      }
    }

    // Remove all items in toRemove set
    toRemove.forEach(itemId => {
      delete newTree[itemId];
    });

    setTree(newTree);
  }, [tree, setTree]);

  /**
   * Move an item (and its entire subtree) to a new parent.
   *
   * Guards against operations that would break the tree:
   * - Moving an item into itself or one of its own descendants (cycles).
   * - Nesting under a leaf that carries a time estimate.
   *
   * Also cascades completion state: re-parenting incomplete work under a
   * completed ancestor marks that ancestor (and its ancestors) incomplete,
   * mirroring the behavior of addChild.
   */
  const moveItem = useCallback((itemId: string, newParentId: string, newIndex?: number) => {
    const item = tree[itemId];
    const newParent = tree[newParentId];

    if (!item || !newParent) return;

    // Cannot move a node into itself.
    if (itemId === newParentId) return;

    // Cannot move a node into one of its own descendants (would create a cycle).
    // findAllParentIds returns every ancestor of newParentId; if itemId is among
    // them, then newParentId lives inside itemId's subtree.
    if (findAllParentIds(tree, newParentId).includes(itemId)) return;

    // The destination must be able to accept children.
    if (!canAddChildToNode(tree, newParentId)) return;

    // Active work should not be nested under an archived task.
    if (newParent.archived) return;

    // Find and remove from current parent
    let oldParentId: string | null = null;
    for (const [parentId, parent] of Object.entries(tree)) {
      if (parent.children.includes(itemId)) {
        oldParentId = parentId;
        break;
      }
    }

    if (!oldParentId) return;

    const newTree = { ...tree };

    // Remove from old parent
    newTree[oldParentId] = {
      ...newTree[oldParentId],
      children: newTree[oldParentId].children.filter(childId => childId !== itemId),
    };

    // Add to new parent
    const newChildren = [...newParent.children];
    if (newIndex !== undefined && newIndex >= 0 && newIndex <= newChildren.length) {
      newChildren.splice(newIndex, 0, itemId);
    } else {
      newChildren.push(itemId);
    }

    newTree[newParentId] = {
      ...newParent,
      children: newChildren,
    };

    // Cascade completion state: moving incomplete work under a done ancestor
    // means that ancestor is no longer complete.
    if (!item.done && newParent.done) {
      const ancestorsToUnmark = [newParentId, ...findAllParentIds(tree, newParentId)];
      ancestorsToUnmark.forEach(pid => {
        if (newTree[pid]) {
          newTree[pid] = { ...newTree[pid], done: false };
        }
      });
    }

    setTree(newTree);
  }, [tree, setTree]);

  const reorderChildren = useCallback((parentId: string, newChildrenOrder: string[]) => {
    const parent = tree[parentId];
    if (!parent) return;

    // Validate that all children are accounted for
    const currentChildren = [...parent.children].sort();
    const newChildren = [...newChildrenOrder].sort();

    if (currentChildren.length !== newChildren.length ||
        !currentChildren.every((child, index) => child === newChildren[index])) {
      console.error('Invalid reorder: children do not match');
      return;
    }

    updateItem(parentId, { children: newChildrenOrder });
  }, [tree, updateItem]);

  // --- Day plan ("Today" list) ----------------------------------------------

  const setToday = useCallback((next: TodayList) => {
    commit(treeRef.current, next);
  }, [commit]);

  /**
   * Add a task to the day's list.
   *
   * Touching the plan stamps it as today's, so a task added straight from the
   * panel does not leave the plan looking like yesterday's leftovers.
   */
  const addToToday = useCallback((id: string) => {
    const current = todayRef.current;
    if (current.items.includes(id)) return;
    if (!isEligibleForToday(treeRef.current, id)) return;

    setToday({ date: getLocalDateString(), items: [...current.items, id] });
  }, [setToday]);

  const removeFromToday = useCallback((id: string) => {
    const current = todayRef.current;
    if (!current.items.includes(id)) return;

    setToday({ ...current, items: current.items.filter(itemId => itemId !== id) });
  }, [setToday]);

  /**
   * Reorder the plan. The new order must be a permutation of the current one —
   * reordering is never allowed to add or drop work.
   */
  const reorderToday = useCallback((items: string[]) => {
    const current = todayRef.current;
    if (items.length !== current.items.length) return;

    const currentSorted = [...current.items].sort();
    const nextSorted = [...items].sort();
    if (!currentSorted.every((id, index) => id === nextSorted[index])) return;

    setToday({ ...current, items });
  }, [setToday]);

  const commitDayPlan = useCallback((items: string[]) => {
    const tree = treeRef.current;
    const seen = new Set<string>();
    const eligible = items.filter(id => {
      if (seen.has(id) || !isEligibleForToday(tree, id)) return false;
      seen.add(id);
      return true;
    });

    setToday({ date: getLocalDateString(), items: eligible });
  }, [setToday]);

  // Staleness is derived from the wall clock, which no state change reflects.
  // Re-checking when the tab regains focus catches the app being left open
  // across midnight.
  const [, setDayTick] = useState(0);
  useEffect(() => {
    const recheck = () => setDayTick(tick => tick + 1);
    window.addEventListener('focus', recheck);
    document.addEventListener('visibilitychange', recheck);
    return () => {
      window.removeEventListener('focus', recheck);
      document.removeEventListener('visibilitychange', recheck);
    };
  }, []);

  const isDayPlanStale = !isTodayListCurrent(today);

  const hasPlannableWork = useMemo(
    () => today.items.length > 0 || Object.keys(tree).some(id => isEligibleForToday(tree, id)),
    [tree, today]
  );

  /**
   * Report actual measured node height from ResizeObserver
   *
   * This updates the height map with DOM-measured values, which will be used
   * in the next layout calculation. Only significant changes (>2px) trigger updates
   * to prevent infinite render loops from sub-pixel differences.
   */
  const reportNodeHeight = useCallback((id: string, height: number) => {
    setActualHeights(prev => {
      const oldHeight = prev.get(id);
      const CHANGE_THRESHOLD = 2; // Only update if difference > 2px

      // Skip update if height is essentially unchanged
      if (oldHeight !== undefined && Math.abs(oldHeight - height) <= CHANGE_THRESHOLD) {
        return prev;
      }

      // Create new map with updated height
      const updated = new Map(prev);
      updated.set(id, height);
      return updated;
    });
  }, []);

  const value: TodoContextType = {
    tree,
    setTree,
    updateItem,
    toggleDone,
    toggleBlocked,
    toggleArchived,
    toggleHighlighted,
    unarchiveItem,
    setPriority,
    setTimeEstimate,
    updateName,
    updateDescription,
    setVisibility,
    addChild,
    removeItem,
    moveItem,
    reorderChildren,
    onTreeChange,
    today,
    isDayPlanStale,
    hasPlannableWork,
    addToToday,
    removeFromToday,
    reorderToday,
    commitDayPlan,
    undo,
    redo,
    canUndo,
    canRedo,
    actualHeights,
    reportNodeHeight,
  };

  return (
    <TodoContext.Provider value={value}>
      {children}
    </TodoContext.Provider>
  );
}